'use strict';
import { AiAnalyzerQuickImageList } from '@components/AiAnalyzer/AiAnalyzerQuickImageList.js';
import { WorkflowIcon } from '@icons/index.js';
import UIIcon from '@icons/UIIcon.js';
import { UIButton, UIDiv, UIText } from '@ui';

class AiAnalyzerEmptyState extends UIDiv {
  // We want to only shows Documents tools when opened in task context
  constructor(
    onOptionSelect,
    isOpenedInTask = false,
    onModeChange = null,
    detectImageSelection = {},
  ) {
    super();
    this.setClass(
      'flex flex-col items-center md:justify-center h-full p-4 pt-2 overflow-y-auto',
    );
    this.onOptionSelect = onOptionSelect;
    this.onModeChange = onModeChange;
    this.detectImageSelection = detectImageSelection;
    this.quickImageList = null;
    this.selectedTool = 0; // Track selected tool index
    this.isOpenedInTask = isOpenedInTask;
    this.currentMode = 'detect'; // 'work', 'chat', or 'detect'. Chat is hidden in this UI.
    this.toolsList = this.#getToolsForContext(isOpenedInTask);

    this.#buildComponent();
  }

  destroy() {
    this.quickImageList?.destroy?.();
    this.quickImageList = null;
  }

  refreshDetectSelection() {
    this.quickImageList?.refreshSelection?.();
  }

  setMode(mode) {
    if (mode === this.currentMode) return;
    this.currentMode = mode;
    this.selectedTool = 0;
    this.toolsList = this.#getToolsForContext(this.isOpenedInTask);
    this.#rebuildComponent();
  }

  #rebuildComponent() {
    this.quickImageList?.destroy?.();
    this.quickImageList = null;
    this.clear();
    this.#buildComponent();
  }

  #buildModeToggle() {
    const toggleContainer = new UIDiv();
    toggleContainer.setClass(
      'flex items-center gap-1 p-1 bg-gray-100 rounded-full mb-3',
    );

    // Workspace Mode button
    this.workModeBtn = new UIButton();
    this.workModeBtn.setClass(
      [
        'flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all',
        this.currentMode === 'work'
          ? 'bg-white text-blue-700 shadow-sm'
          : 'text-gray-500 hover:text-gray-700',
      ].join(' '),
    );
    const workIcon = new WorkflowIcon({
      size: 16,
      color: 'none',
      stroke: 'currentColor',
    });
    const workText = new UIText(i18next.t('sidebar.aiAnalyzer.mode.work'));
    this.workModeBtn.add(workIcon);
    this.workModeBtn.add(workText);
    this.workModeBtn.onClick(() => this.#handleModeSwitch('work'));

    // Detect Mode button
    this.detectModeBtn = new UIButton();
    this.detectModeBtn.setClass(
      [
        'flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all',
        this.currentMode === 'detect'
          ? 'bg-white text-purple-700 shadow-sm'
          : 'text-gray-500 hover:text-gray-700',
      ].join(' '),
    );
    const detectIcon = new UIIcon('scan-eye', {
      size: 16,
      color: 'none',
      strokeColor: 'currentColor',
    });
    const detectText = new UIText(i18next.t('sidebar.aiAnalyzer.mode.detect'));
    this.detectModeBtn.add(detectIcon);
    this.detectModeBtn.onClick(() => this.#handleModeSwitch('detect'));
    this.detectModeBtn.add(detectText);

    toggleContainer.add(this.workModeBtn);
    toggleContainer.add(this.detectModeBtn);

    return toggleContainer;
  }

  #handleModeSwitch(mode) {
    if (mode === this.currentMode) return;
    this.currentMode = mode;
    this.selectedTool = 0;
    this.toolsList = this.#getToolsForContext(this.isOpenedInTask);
    this.#rebuildComponent();
    if (this.onModeChange) {
      this.onModeChange(mode);
    }
  }

  #buildComponent() {
    // Mode toggle at the top
    const modeToggle = this.#buildModeToggle();
    this.add(modeToggle);

    // Mode description
    const modeDescription = new UIText(
      this.currentMode === 'detect'
        ? i18next.t('sidebar.aiAnalyzer.mode.detect_description')
        : this.currentMode === 'work'
          ? i18next.t('sidebar.aiAnalyzer.mode.work_description')
          : i18next.t('sidebar.aiAnalyzer.mode.chat_description'),
    );
    modeDescription.setClass('text-sm text-gray-600 mb-6 text-center max-w-md');
    // this.add(modeDescription);

    // Welcome message
    const welcomeMessage = new UIText(
      i18next.t('sidebar.aiAnalyzer.welcome_screen.welcome_message'),
    );
    welcomeMessage.setClass('text-2xl font-semibold mb-4 text-center');
    this.add(welcomeMessage);

    // Tools container
    const toolsContainer = new UIDiv();
    toolsContainer.setClass(
      'flex flex-row flex-wrap gap-2.5 text-sm max-sm:justify-evenly mb-2.5',
    );

    // Create tool buttons
    this.toolsList.forEach((tool, index) => {
      const toolButton = new UIButton(tool.title);
      toolButton.setClass(
        [
          'relative text-white flex items-center gap-1 sm:gap-2 rounded-xl justify-center whitespace-nowrap text-base shadow transition-colors px-5 py-3 font-semibold h-10 max-sm:flex-col',
          'bg-[var(--o-hive-primary-80)] hover:bg-[var(--o-hive-primary-60)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          'data-[selected=false]:bg-[var(--o-hive-light-60)] data-[selected=false]:hover:bg-[var(--o-hive-primary-60)] data-[selected=false]:text-black',
          'disabled:!bg-[var(--button-disabled)] disabled:hover:!bg-[var(--button-disabled)] disabled:!text-gray-400 disabled:!cursor-not-allowed',
        ].join(' '),
      );

      if (tool.disabled) {
        toolButton.setDisabled(true);
      }

      toolButton.setAttribute(
        'data-selected',
        index === this.selectedTool ? 'true' : 'false',
      );

      toolButton.onClick(() => {
        // Update selected state for all buttons
        toolsContainer.getChildren().forEach((btn, i) => {
          btn.setAttribute('data-selected', i === index ? 'true' : 'false');
        });
        this.selectedTool = index;
        this.#updateOptionsDisplay();
      });

      toolsContainer.add(toolButton);
    });

    this.add(toolsContainer);

    if (this.currentMode === 'detect') {
      this.quickImageList = new AiAnalyzerQuickImageList({
        getSelection: () => this.detectImageSelection.getSelection?.() || {},
        onSelect: image => {
          this.detectImageSelection.onSelect?.(image);
        },
      });
      this.add(this.quickImageList);
    }

    // Options container
    this.optionsContainer = new UIDiv();
    this.optionsContainer.setClass('w-full mt-1 max-w-lg');
    this.add(this.optionsContainer);

    // Initialize options display
    this.#updateOptionsDisplay();
  }

  #updateOptionsDisplay() {
    this.optionsContainer.clear();
    const selectedToolOptions = this.toolsList[this.selectedTool].options;

    if (selectedToolOptions && selectedToolOptions.length > 0) {
      selectedToolOptions.forEach(option => {
        const optionDiv = new UIDiv();
        optionDiv.setClass(
          'flex items-start gap-2 border-t border-[var(--o-hive-light-80)] py-1 first:border-none',
        );

        const optionButton = new UIButton(option.value);
        optionButton.setClass(
          'text-lg font-medium w-full rounded-md py-2 text-left hover:bg-[var(--o-hive-light-60)] px-1 sm:px-3 hover:!filter-none',
        );

        optionButton.onClick(() => {
          this.onOptionSelect(option.value);
        });

        optionDiv.add(optionButton);
        this.optionsContainer.add(optionDiv);
      });
    }
  }

  #getToolsForContext(isTaskContext) {
    if (this.currentMode === 'detect') {
      return [
        {
          title: i18next.t('sidebar.aiAnalyzer.mode.detect') || 'Detect',
          options: [],
        },
      ];
    }

    // Chat mode - show conversational AI options
    if (this.currentMode === 'chat') {
      return [
        {
          title:
            i18next.t(
              'sidebar.aiAnalyzer.welcome_screen.chat_tools.general.title',
            ) || 'General',
          options: [
            {
              key: 'explain',
              value:
                i18next.t(
                  'sidebar.aiAnalyzer.welcome_screen.chat_tools.general.explain',
                ) || 'Explain a concept to me',
            },
            {
              key: 'brainstorm',
              value:
                i18next.t(
                  'sidebar.aiAnalyzer.welcome_screen.chat_tools.general.brainstorm',
                ) || 'Help me brainstorm ideas',
            },
            {
              key: 'summarize',
              value:
                i18next.t(
                  'sidebar.aiAnalyzer.welcome_screen.chat_tools.general.summarize',
                ) || 'Summarize information for me',
            },
          ],
        },
        {
          title: i18next.t(
            'sidebar.aiAnalyzer.welcome_screen.chat_tools.shopping.title',
          ),
          options: [
            {
              key: 'draft',
              value: i18next.t(
                'sidebar.aiAnalyzer.welcome_screen.chat_tools.shopping.product_recommendation',
              ),
            },
            {
              key: 'improve',
              value: i18next.t(
                'sidebar.aiAnalyzer.welcome_screen.chat_tools.shopping.price_comparison',
              ),
            },
            {
              key: 'translate',
              value: i18next.t(
                'sidebar.aiAnalyzer.welcome_screen.chat_tools.shopping.deal_finding',
              ),
            },
          ],
        },
        {
          title: i18next.t(
            'sidebar.aiAnalyzer.welcome_screen.chat_tools.travel.title',
          ),
          options: [
            {
              key: 'question',
              value: i18next.t(
                'sidebar.aiAnalyzer.welcome_screen.chat_tools.travel.flights',
              ),
            },
            {
              key: 'compare',
              value: i18next.t(
                'sidebar.aiAnalyzer.welcome_screen.chat_tools.travel.hotels',
              ),
            },
            {
              key: 'advice',
              value: i18next.t(
                'sidebar.aiAnalyzer.welcome_screen.chat_tools.travel.places',
              ),
            },
          ],
        },
      ];
    }

    // work mode - original tools for project management
    this.toolsList = [
      {
        title: 'Projects',
        options: [
          {
            key: 'create_project',
            value: 'Create a new project',
          },
          {
            key: 'list_projects',
            value: 'Show all projects with deadlines this month',
          },
          {
            key: 'delete_project',
            value: 'Remove a project',
          },
        ],
      },
      {
        title: 'Tasks',
        options: [
          {
            key: 'create_task',
            value: 'Add a new task',
          },
          {
            key: 'create_task_report',
            value: 'Generate a task report',
          },
          {
            key: 'find_task',
            value: 'View all tasks currently in progress',
          },
        ],
      },
      {
        title: i18next.t('sidebar.aiAnalyzer.welcome_screen.tools.files.title'),
        options: [
          {
            key: 'summarize',
            value: i18next.t(
              'sidebar.aiAnalyzer.welcome_screen.tools.files.summarize',
            ),
          },
          {
            key: 'create_report',
            value: i18next.t(
              'sidebar.aiAnalyzer.welcome_screen.tools.files.create_report',
            ),
          },
        ],
      },
    ];
    // if (isTaskContext) {
    //   // Exclude 'Projects' and 'Tasks' tools when not in task context
    //   return this.toolsList.filter(
    //     tool => tool.title !== 'Projects' && tool.title !== 'Tasks',
    //   );
    // } else {
    return this.toolsList;
    // }
  }
}

export { AiAnalyzerEmptyState };
